export type AccessLocationsField = 'leadsAccessControlLocationIds' | 'opportunitiesAccessControlLocationIds';

export type AccessLocationsFields = Partial<Record<AccessLocationsField, string[]>>;

export type AccessLocationsSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

type Listener = () => void;

interface UserSaveState {
  pending: AccessLocationsFields;
  timer?: ReturnType<typeof setTimeout>;
  inFlight: boolean;
  statuses: Partial<Record<AccessLocationsField, AccessLocationsSaveStatus>>;
}

interface Options {
  save: (userId: string, fields: AccessLocationsFields) => Promise<unknown>;
  delayMs: number;
}

export interface AccessLocationsAutosaver {
  queue: (userId: string, fields: AccessLocationsFields) => void;
  flushAll: () => void;
  hasUnsavedChanges: () => boolean;
  getStatus: (userId: string, field: AccessLocationsField) => AccessLocationsSaveStatus;
  subscribe: (listener: Listener) => () => void;
}

/**
 * Debounces access-location saves per user (not globally), merges both columns of the same user
 * into one mutation and never runs two saves for the same user concurrently, so no edit is dropped
 * and a stale request can't overwrite a newer one.
 */
export const createAccessLocationsAutosaver = ({ save, delayMs }: Options): AccessLocationsAutosaver => {
  const users = new Map<string, UserSaveState>();
  const listeners = new Set<Listener>();

  const notify = () => listeners.forEach(listener => listener());

  const getUser = (userId: string) => {
    let user = users.get(userId);
    if (!user) {
      user = { pending: {}, inFlight: false, statuses: {} };
      users.set(userId, user);
    }
    return user;
  };

  const send = (userId: string) => {
    const user = getUser(userId);
    const fields = user.pending;
    const sentKeys = Object.keys(fields) as AccessLocationsField[];
    if (user.inFlight || sentKeys.length === 0) return;

    user.pending = {};
    user.inFlight = true;
    sentKeys.forEach(key => (user.statuses[key] = 'saving'));
    notify();

    const settle = (status: AccessLocationsSaveStatus) => {
      user.inFlight = false;
      // A newer edit queued while this request was in flight owns the status of its field
      sentKeys.filter(key => !(key in user.pending)).forEach(key => (user.statuses[key] = status));
      if (!user.timer) send(userId);
      notify();
    };

    save(userId, fields).then(
      () => settle('saved'),
      () => settle('error')
    );
  };

  return {
    queue: (userId, fields) => {
      const user = getUser(userId);
      user.pending = { ...user.pending, ...fields };
      (Object.keys(fields) as AccessLocationsField[]).forEach(key => (user.statuses[key] = 'pending'));
      if (user.timer) clearTimeout(user.timer);
      user.timer = setTimeout(() => {
        user.timer = undefined;
        send(userId);
      }, delayMs);
      notify();
    },
    flushAll: () => {
      users.forEach((user, userId) => {
        if (user.timer) {
          clearTimeout(user.timer);
          user.timer = undefined;
        }
        send(userId);
      });
    },
    hasUnsavedChanges: () =>
      Array.from(users.values()).some(user => user.inFlight || Object.keys(user.pending).length > 0),
    getStatus: (userId, field) => users.get(userId)?.statuses[field] ?? 'idle',
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};
